/* eslint-disable sonarjs/cognitive-complexity, react/forbid-component-props */
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import cookies from 'react-cookies';
import { useSelector, useDispatch } from 'react-redux';
import { object, string, ref, ValidationError } from 'yup';
import { COOKIES_CONFIG, passwordRules } from '../../brands/default/config';
import { COOKIES, DEFAULT_LOCATION } from '../../brands/default/constants';
import { REGISTRATION_FORM } from '../../brands/default/content/components/registration';
import { REQUIRED_FIELD } from '../../brands/default/content/helpers';
import {
  AUTO_ADDRESS_LOOKUP,
  GOOGLE_ENABLED,
  TEXT_LOWERCASE,
} from '../../brands/default/features';
import { LINK_HOME } from '../../brands/default/links';
import { URLTRACKOPENRAFLINK } from '../../brands/default/services';
import { verifyPostcode } from '../../helpers/address';
import { trackEvent, trackPage } from '../../helpers/analytics';
import { setCookie } from '../../helpers/cookieSetter';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import httpCommonHeaders from '../../helpers/httpCommonHeaders';
import {
  getAutocompleteTestValue,
  isUserInPasswordlessCheckoutTest,
} from '../../helpers/user';
import { getUserStatus } from '../../helpers/userStatus';
import {
  renderPasswordErrors,
  validatePasswordRules,
  validatePostCode,
} from '../../helpers/validations';
import { setUserEmail } from '../../redux/actions/email';
import { setLoadingState } from '../../redux/actions/loading';
import { registerUser, loginSuccess } from '../../redux/actions/user';
import { LOADING_STATUS } from '../../redux/reducers/loading';
import Message from '../_generic/Message';
import axios, { sendBotAlert } from '../_generic/axiosSplunk/axiosSplunk';
import ButtonForm from '../_generic/button/CTAButton';
import FormikInput from '../_generic/input/FormikInput';
import TextDevider from '../_generic/textDevider/TextDevider';
import useToast from '../_generic/toast/UseToast';
import BillingAddress from '../address/BillingAddress';
import BillingAddressAutocomplete from '../address/BillingAddressAutocomplete';
import BillingAddressOnlyPostcode from '../address/BillingAddressOnlyPostcode';
import { panel } from '../checkout/details/UserPanelSetup';
import PrivacyText from '../login/PrivacyText';
import GoogleButton from '../mediaButtons/GoogleButton';
import SocialMessage from './SocialMessage';

const schema = ({
  autoAddressLookup,
  isManualAddress,
  isPasswordless,
  isPostCodeVerification,
}) =>
  object().shape({
    addressLine1: autoAddressLookup
      ? string()
      : string().required(REGISTRATION_FORM.requiredField),
    addressLine2: string(),
    city: autoAddressLookup
      ? string()
      : string().required(REGISTRATION_FORM.requiredField),
    email: string()
      .required(REGISTRATION_FORM.requiredField)
      .email(REGISTRATION_FORM.enterValidEmail),
    firstName: string().required(REGISTRATION_FORM.requiredField),
    password: isPasswordless
      ? null
      : string()
          .required(REQUIRED_FIELD)
          .test('password', '', (value) => {
            const errors = validatePasswordRules(value, passwordRules);
            if (errors.length > 0) {
              return new ValidationError(errors.join('|'), value, 'password');
            }

            return true;
          }),
    passwordConfirmation: isPasswordless
      ? null
      : string()
          .required(REGISTRATION_FORM.requiredField)
          .min(6, REGISTRATION_FORM.passwordSixCharacters)
          .when('password', {
            is: (value) => Boolean(value && value.length > 0),
            then: string().oneOf(
              [ref('password')],
              REGISTRATION_FORM.matchPassword,
            ),
          }),
    postCode: autoAddressLookup
      ? isPostCodeVerification && !isManualAddress
        ? string()
            .required(REGISTRATION_FORM.requiredField)
            .test('postCode', REGISTRATION_FORM.verifyPostcode, (value) =>
              verifyPostcode(value),
            )
        : string()
            .required(REGISTRATION_FORM.requiredField)
            .test('postCode', REGISTRATION_FORM.enterValidPostcode, (value) =>
              validatePostCode(value),
            )
      : string(),
    referralCode: string(),
    surname: string().required(REGISTRATION_FORM.requiredField),
  });

const FormRegistration = ({
  setActivePanel,
  isCheckout = false,
  isHighFraudRiskDealInBasket = false,
  isRegistrationPage = false,
  vipPaywall = false,
}) => {
  const isUserInPasswordlessTest = isUserInPasswordlessCheckoutTest();
  const autoCompleteTestCookie = getAutocompleteTestValue();
  const isUserInAutocompleteTest = autoCompleteTestCookie === 'b';
  const isUserInAutocompleteTestC = autoCompleteTestCookie === 'c';
  const isPasswordless = isUserInPasswordlessTest && isCheckout;
  const [locations, userEmail] = useSelector((state) => [
    state.locations,
    state.email,
  ]);

  const {
    anErrorOccured,
    confirmPassword,
    firstName,
    formEmail,
    formPassword,
    lastName,
    rafAccountCreationInfo,
    registerBottomFirst,
    registerButtonText,
    registerPrivacyPolicy,
    registerPrivacyPolicyRecurring,
    registerTop,
    registeredSuccesfully,
    signUpWithGoogle,
    tryLater,
    userExistInWowcher,
  } = REGISTRATION_FORM;

  const extraClasses = `mobile-highlight-border ${
    TEXT_LOWERCASE && 'lowercase_placeholder'
  }`;

  // original method is default
  const [isAddressLookup, setIsAddressLookup] = useState(true);
  const [isAutocomplete, setIsAutocomplete] = useState(false);
  const [isPostCodeVerification, setIsPostCodeVerification] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);

  const [isShowError, setShowError] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [botField, setBotField] = useState('');
  const router = useRouter();
  const [referralCode, setReferralCode] = useState(router?.query?.referralCode);
  const [emailOpt, setEmailOpt] = useState(false);

  const toast = useToast();
  const dispatch = useDispatch();

  const initialValues = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    email: userEmail.userEmail || '',
    firstName: '',
    postCode: '',
    surname: '',
  };
  if (!isPasswordless) {
    initialValues.password = '';
    initialValues.passwordConfirmation = '';
  }
  const [formInitialValues, setFormInitialValues] = useState(initialValues);

  const showErrorToast = (message) => {
    toast.addToast(message, 'toast-error');
  };

  const handleHPField = (event) => {
    setBotField(event.target.value);
  };

  const handleManualAddress = (value, postcode, values) => {
    if (postcode && values) {
      // if the form initialvalues changes and form has validateOnMount, validation is triggered immediatly
      setFormInitialValues(values);
    }
    setIsManualAddress(value);
  };

  useEffect(() => {
    if (isRegistrationPage || vipPaywall) {
      setIsAddressLookup(false);
      setIsAutocomplete(true);
      setIsPostCodeVerification(false);
    }
    if (isCheckout) {
      if (isUserInAutocompleteTestC === true) {
        setIsAddressLookup(false);
        setIsAutocomplete(true);
        setIsPostCodeVerification(false);
      } else if (isUserInAutocompleteTest === true) {
        // set postcode verification
        setIsAddressLookup(false);
        setIsAutocomplete(false);
        setIsPostCodeVerification(true);
        // set autocomplete
        if (isHighFraudRiskDealInBasket) {
          setIsAddressLookup(false);
          setIsAutocomplete(true);
          setIsPostCodeVerification(false);
        }
      } else {
        setIsAddressLookup(true);
        setIsAutocomplete(false);
        setIsPostCodeVerification(false);
      }
    }
  }, [isRegistrationPage, isCheckout, isHighFraudRiskDealInBasket]);

  useEffect(() => {
    setCookie({ key: COOKIES.emailOptOut, value: false });
  }, []);

  useEffect(() => {
    async function trackOpenRafLink() {
      try {
        return await axios(
          `${URLTRACKOPENRAFLINK}?referralCode=${referralCode}`,
          {
            cache: false,
            headers: httpCommonHeaders(),
            method: 'PUT',
            withCredentials: false,
          },
        );
      } catch {
        return null;
      }
    }

    if (referralCode) {
      setCookie({
        expire: COOKIES_CONFIG.COOKIES_EXPIRES_MONTH,
        key: COOKIES.referralCode,
        value: referralCode,
      });
      trackOpenRafLink();
    }
  }, [referralCode]);

  useEffect(() => {
    setReferralCode(() => cookies.load(COOKIES.referralCode));
  }, []);

  useEffect(() => {
    const emailToggle = document.querySelector('#emailToggle');
    const handler = () => {
      setEmailOpt(!emailOpt);
      setCookie({ key: COOKIES.emailOptOut, value: !emailOpt });
    };
    if (emailToggle) {
      emailToggle.addEventListener('click', handler);

      return () => {
        emailToggle.removeEventListener('click', handler);
      };
    }

    return null;
  });

  const sendForm = async (values) => {
    if (
      botField.length > 0 ||
      document.querySelector('#alt_email').value.length > 0
    ) {
      router.push(LINK_HOME);
      sendBotAlert(router);

      return;
    }

    if (
      (values.addressLine1 === '' || values.city === '') &&
      !isPostCodeVerification
    ) {
      setShowError(true);

      return;
    }

    dispatch(setLoadingState(LOADING_STATUS.LOADING));

    values.title = '';

    // Email confirmation is dynamically set to equal the email field
    values.emailConfirmation = values.email;

    // Set Referral Code incase the user is coming from refer a friend.
    values.referralCode = referralCode;

    // Other properties absent from the form but required by the API
    values.rejectDMGTContact = false;

    // Get subscriptionSource from cookie if it exists
    const subscriptionCookie = cookies.load(COOKIES.userSource);
    values.subscriptionSource = subscriptionCookie
      ? decodeURIComponent(subscriptionCookie)
      : null;

    // Get user location from cookie if it exists

    values.location = locations?.location?.shortName
      ? locations.location.shortName
      : DEFAULT_LOCATION.name;

    // Code to optout from marketing email
    values.emailOptOut = emailOpt;

    // don't send autocomplete field to BE
    // eslint-disable-next-line fp/no-delete
    delete values.autocomplete;
    try {
      const response = await registerUser(values, isPasswordless);

      dispatch(setLoadingState(LOADING_STATUS.READY));

      if (response.data) {
        toast.addToast(
          response.data.message || registeredSuccesfully,
          'toast-success',
        );
        if (vipPaywall) {
          trackEvent('viphub: paywall register');
        }
        setCookie(
          COOKIES.brandCookie,
          'subscribed',
          COOKIES_CONFIG.COOKIE_SUBSCRIBED_EXPIRES,
        );
        if (referralCode) {
          setCookie({
            expire: COOKIES_CONFIG.COOKIES_EXPIRES_DAY,
            key: COOKIES.referralRegistered,
            value: true,
          });
          trackPage({
            pageName: 'my account: register: refer a friend',
          });
          trackEvent('my account: register: refer a friend');
        }
        trackEvent('completed_registration');
        if (isSubscribed) {
          trackEvent('repeatEmailEvent');
        } else {
          trackEvent('newEmailEvent');
        }
        trackEvent(emailOpt ? 'emailoptout' : 'emailoptin');
        dispatch(loginSuccess(response.data));
      } else {
        if (response.fieldErrors) {
          Object.values(response.fieldErrors).forEach((errorValue) => {
            showErrorToast(anErrorOccured + ' ' + errorValue);
          });

          return;
        }
        showErrorToast(response.message || tryLater);
      }
    } catch (error) {
      dispatch(setLoadingState(LOADING_STATUS.READY));
      showErrorToast(error.message || tryLater);
      ErrorTrackingService.logError(error);
    }
  };

  const setUserDetails = async (email) => {
    try {
      const response = await getUserStatus(email);
      setIsSubscribed(response.status.isSubscribed);

      if (response.isNewUser) return;
      if (response.userEmail) dispatch(setUserEmail(response.userEmail));
      if (response.status.isRegistered && response.status.hasPassword) {
        setActivePanel(panel.login);
        toast.addToast(userExistInWowcher, 'toast-info');
      }
      if (response.status.isRegistered && !response.status.hasPassword) {
        // user is returning with no password set, will need to confirm email
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        cookies.save(COOKIES.confirmEmailRequired, 'true', {
          domain: `.${process.env.NEXT_PUBLIC_DOMAIN_NAME}`,
          expires,
          path: '/',
          samesite: 'none',
        });
      }
    } catch (error) {
      ErrorTrackingService.logError(error);
      showErrorToast(error.message || anErrorOccured);
    }
  };

  return (
    <>
      {referralCode ? (
        <Message message={rafAccountCreationInfo} type={'INFO'} />
      ) : null}
      {!isCheckout && GOOGLE_ENABLED && (
        <>
          <PrivacyText text={registerTop} />
        </>
      )}

      {GOOGLE_ENABLED && (
        <div className="text-center">
          <GoogleButton
            checkout={isCheckout}
            registration
            title={signUpWithGoogle}
          />
        </div>
      )}

      {GOOGLE_ENABLED && <TextDevider vipPaywall={vipPaywall} />}
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        onSubmit={async (values) => sendForm(values)}
        validateOnBlur
        validateOnChange
        validateOnMount
        validationSchema={schema({
          autoAddressLookup: AUTO_ADDRESS_LOOKUP,
          isManualAddress,
          isPasswordless,
          isPostCodeVerification,
        })}
      >
        {({
          handleChange,
          handleSubmit,
          touched,
          handleBlur,
          setValues,
          isSubmitting,
          isValid,
          values,
          errors,
        }) => {
          /*           const isShowHint = isPasswordless
            ? false
            : values.password.length < PASSWORD_MIN_LENGTH; */
          const passwordErrors = renderPasswordErrors(errors.password);
          const blurFunction = (event) => {
            const value = event.target.value;
            const name = event.target.name;

            if (name === 'email') trackEvent('email_field');
            if (name === 'firstName') trackEvent('first_name_field');
            if (name === 'surname') trackEvent('surname_field');
            if (name === 'password') trackEvent('password_field');
            if (name === 'passwordConfirmation')
              trackEvent('password_confirmation_field');

            handleBlur(event);
            if (!value) return;
            if (name === 'email' && !errors.email) setUserDetails(value);
          };

          return (
            <form
              className="row"
              name="registration-form"
              onSubmit={handleSubmit}
            >
              <div className="col-12">
                {values.email === '' && <SocialMessage />}
                <FormikInput
                  dataqa="email"
                  errorText={errors.email}
                  extraClasses={extraClasses}
                  icon={faEnvelope}
                  inputError={errors.email}
                  inputTouched={touched.email}
                  inputValue={values.email}
                  name="email"
                  onBlurFunction={(event) => blurFunction(event)}
                  onChangeFunction={handleChange}
                  placeholder={formEmail}
                  type="email"
                />
              </div>
              <div className="col-12 col-md-6">
                <FormikInput
                  dataqa="firstName"
                  errorText={errors.firstName}
                  extraClasses={extraClasses}
                  inputError={errors.firstName}
                  inputTouched={touched.firstName}
                  inputValue={values.firstName}
                  name="firstName"
                  onBlurFunction={(event) => blurFunction(event)}
                  onChangeFunction={handleChange}
                  placeholder={firstName}
                  type="text"
                />
              </div>
              <div className="col-12 col-md-6">
                <FormikInput
                  dataqa="lastName"
                  errorText={errors.surname}
                  extraClasses={extraClasses}
                  inputError={errors.surname}
                  inputTouched={touched.surname}
                  inputValue={values.surname}
                  name="surname"
                  onBlurFunction={(event) => blurFunction(event)}
                  onChangeFunction={handleChange}
                  placeholder={lastName}
                  type="text"
                />
              </div>
              {isAddressLookup && (
                <BillingAddress
                  errors={errors}
                  extraClasses={extraClasses}
                  isRegistrationPage
                  isShowError={isShowError}
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  onManualAddress={handleManualAddress}
                  setValues={setValues}
                  touched={touched}
                  values={values}
                />
              )}
              {isAutocomplete && (
                <BillingAddressAutocomplete
                  errors={errors}
                  extraClasses={extraClasses}
                  isRegistrationPage
                  isShowError={isShowError}
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  onManualAddress={handleManualAddress}
                  setValues={setValues}
                  touched={touched}
                  values={values}
                />
              )}
              {isPostCodeVerification && (
                <BillingAddressOnlyPostcode
                  errors={errors}
                  extraClasses={extraClasses}
                  isRegistrationPage
                  isShowError={isShowError}
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  onManualAddress={handleManualAddress}
                  setValues={setValues}
                  touched={touched}
                  values={values}
                />
              )}
              <input
                aria-label="alt email"
                autoComplete="off"
                className="hp-container"
                id="alt_email"
                name="alt_email"
                onChange={handleHPField}
                tabIndex="-1"
                type="text"
              />

              {!isPasswordless && (
                <>
                  <div className="col-12">
                    <FormikInput
                      autoComplete="new-password"
                      dataqa="password"
                      errorText={passwordErrors}
                      extraClasses={extraClasses}
                      icon={faLock}
                      inputError={errors.password}
                      inputTouched={touched.password}
                      inputValue={values.password}
                      name="password"
                      onBlurFunction={(event) => blurFunction(event)}
                      onChangeFunction={handleChange}
                      placeholder={formPassword}
                      type="password"
                    />
                  </div>
                  <div className="col-12">
                    <FormikInput
                      autoComplete="new-password"
                      dataqa="confirmPassword"
                      errorText={errors.passwordConfirmation}
                      extraClasses={extraClasses}
                      icon={faLock}
                      inputError={errors.passwordConfirmation}
                      inputTouched={touched.passwordConfirmation}
                      inputValue={values.passwordConfirmation}
                      name="passwordConfirmation"
                      onBlurFunction={(event) => blurFunction(event)}
                      onChangeFunction={handleChange}
                      placeholder={confirmPassword}
                      type="password"
                    />
                  </div>
                </>
              )}
              <div className="col-12">
                <ButtonForm
                  className="full-width button register-button"
                  dataqa="proceed"
                  disabled={isSubmitting || !isValid}
                  type="submit"
                >
                  {registerButtonText}
                </ButtonForm>
              </div>
            </form>
          );
        }}
      </Formik>
      <style jsx>{`
        .hp-container {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
          height: 0;
          width: 0;
          z-index: -1;
        }
      `}</style>
      {isCheckout ? (
        <PrivacyText
          text={
            vipPaywall
              ? registerPrivacyPolicy
              : registerPrivacyPolicyRecurring(emailOpt ? "- I'M OUT!" : '')
          }
        />
      ) : (
        <>
          <PrivacyText text={registerBottomFirst} />
        </>
      )}
    </>
  );
};

FormRegistration.propTypes = {
  isCheckout: PropTypes.bool,
  isHighFraudRiskDealInBasket: PropTypes.bool,
  isRegistrationPage: PropTypes.bool,
  setActivePanel: PropTypes.func,
  vipPaywall: PropTypes.bool,
};

export default FormRegistration;
